import { ActivityState } from '@/api/models/activity'
import { ActivityPart } from '@/api/models/activityParts'
import { ActivityWithAggregate } from '@/api/models/aggregate'
import { workflowStateUtils } from '@/utils/workflowStateUtils'
import {
  checkAfterAccessibleUntil,
  checkAfterDueDate,
  checkAfterGracePeriodUntil,
  checkAfterStartUntil,
  checkBeforeStartFrom,
} from './checkConstraintDates'
import { checkReviewVisibility } from './checkReviewVisibility'

export const activityConstraintValidation = (activityPart: ActivityPart<ActivityWithAggregate>) => {
  const beforeStartFrom = checkBeforeStartFrom(activityPart)
  const afterStartUntil = checkAfterStartUntil(activityPart)
  const afterAccessibleUntil = checkAfterAccessibleUntil(activityPart)
  const afterGracePeriodUntil = checkAfterGracePeriodUntil(activityPart)
  const { isReviewHidden, reviewHiddenReason } = checkReviewVisibility(activityPart)

  // due date is not checked in UI. it's just for information
  const afterDueDate = checkAfterDueDate(activityPart)

  const isInProgress = workflowStateUtils.isInProgress(
    activityPart.childActivity?.activityWorkflowAggregate?.lastActivityWorkflow,
  )

  const isFinished = workflowStateUtils.isFinished(
    activityPart.childActivity?.activityWorkflowAggregate?.lastActivityWorkflow,
  )

  const contentUnavailable = !activityPart.contentAvailable

  const baseDisabled = contentUnavailable || beforeStartFrom || afterAccessibleUntil || isReviewHidden

  if (isFinished) {
    return {
      disabled: baseDisabled,
      contentUnavailable,
      beforeStartFrom,
      afterStartUntil: false,
      afterAccessibleUntil,
      afterGracePeriodUntil: false,
      afterDueDate, // For finished state we don't show due to learner, but for future usages it's better to keep it here
      isReviewHidden,
      reviewHiddenReason,
    }
  }

  if (isInProgress) {
    return {
      disabled: baseDisabled || afterGracePeriodUntil,
      contentUnavailable,
      beforeStartFrom,
      afterStartUntil: false,
      afterAccessibleUntil,
      afterGracePeriodUntil,
      afterDueDate,
      isReviewHidden,
      reviewHiddenReason,
    }
  }

  return {
    disabled: baseDisabled || afterStartUntil || afterGracePeriodUntil,
    contentUnavailable,
    beforeStartFrom,
    afterStartUntil,
    afterAccessibleUntil,
    afterGracePeriodUntil,
    afterDueDate,
    isReviewHidden,
    reviewHiddenReason,
  }
}

export const activityConstraintValidationText = (activityPart: ActivityPart<ActivityWithAggregate>) => {
  const {
    disabled,
    contentUnavailable,
    beforeStartFrom,
    afterStartUntil,
    afterAccessibleUntil,
    afterGracePeriodUntil,
    afterDueDate,
    isReviewHidden,
    reviewHiddenReason,
  } = activityConstraintValidation(activityPart)

  const isStateArchivedOrDraft = (state: ActivityState) =>
    state === ActivityState.archived || state === ActivityState.draft

  const isDraftOrArchive = isStateArchivedOrDraft(activityPart.childActivity.state)
  const isNotAvailable = !activityPart.available

  const reasonText = () => {
    switch (true) {
      case isDraftOrArchive:
        return 'compositions.comingSoon'
      case isNotAvailable:
        return 'compositions.completePreviousToPass'
      case !disabled:
        return ''
      case afterGracePeriodUntil:
        return 'compositions.constraintValidations.afterGracePeriodUntil'
      case afterAccessibleUntil:
        return 'compositions.constraintValidations.afterAccessibleUntil'
      case beforeStartFrom:
        return 'compositions.constraintValidations.beforeStartFrom'
      case afterStartUntil:
        return 'compositions.constraintValidations.afterStartUntil'
      case isReviewHidden:
        return reviewHiddenReason || ''
      default:
        return 'compositions.constraintValidations.serverSide'
    }
  }

  return {
    disabled,
    disabledReason: reasonText(),
    contentUnavailable,
    isDraftOrArchive,
    isNotAvailable,
    beforeStartFrom,
    afterStartUntil,
    afterAccessibleUntil,
    afterGracePeriodUntil,
    afterDueDate,
    isReviewHidden,
  }
}

export type ActivityConstraintValidationTextReturn = ReturnType<typeof activityConstraintValidationText>
